import { Socket } from "socket.io-client";
import { useEffect, useMemo, useState } from "react";

import { EmoteAssetType, SignAssetType } from "@app/api/models/user";
import { DefaultProductsType, useDefaultProducts } from "@app/api/product";

import { AppColors } from "@app/constants/Colors";

import { User } from "@app/types/user";
import { Answers, ClassicAnswer, Question } from "@app/types/question";

import { CharacterStatusType } from "@app/utils/character";

import styles from "@app/assets/styles/game.module.scss";
import { EmoteType } from "@app/screens/Game";

interface CharacterProps {
    user: User;
    characterData: DefaultProductsType;
    status: CharacterStatusType;
    textToDisplay?: string;
    showEmote: EmoteType[];
}

function Character(props: CharacterProps) {
    const { status, user, textToDisplay, characterData, showEmote } = props;

    const [currentState, setCurrentState] = useState<CharacterStatusType>();
    const [ scrollValue, setScrollValue ] = useState<number>(50);

    useEffect(() => {
        if (status !== "answer") {
            setCurrentState(status);
        } else if (currentState === "buzz") {
            setCurrentState(status);
        }
    }, [status]);

    const scoreBuzz = currentState === "buzz" ? "" : textToDisplay;

    const characterImg = characterData.characters
        .find((character) => {
            const id = character.id?.split("-")[0];
            if (id) return id === user.character;
        })
        ?.products.find((product) => product.id === user.characterSkinId)
        ?.assets.find((asset) => asset.type === status);

    const signImg = characterData?.sign.products?.find(
        (product) => product.id === user.signSkinId,
    )?.assets;

    const panelImg = signImg?.find((asset) => asset.type === SignAssetType.Panel);

    const tieImg = signImg?.find((asset) => asset.type === SignAssetType.Tie);

    const characterEmoteImage = useMemo(() => {
        if (showEmote.length > 0 && characterData?.emote) {
            const emote = showEmote.find((emote) => emote.userId === user.id);
            if (!emote) return;
            const emoteImages = characterData.emote.products.find(
                (emoteProduct) => emoteProduct.id === emote?.emoteId,
            )?.assets;
            return emoteImages?.find(
                (asset) => asset.type === EmoteAssetType.Emote,
            )?.url;
        }
    }, [showEmote]);

    useEffect(() => {
        if (characterEmoteImage) {
            setScrollValue(150);
            setTimeout(() => {
                setScrollValue(50);
            }, 5500);
        }
    }, [characterEmoteImage]);

    return (
        <div className={styles.characterInfoWrapper}>
            <div className={styles.sign}>
                <div
                    className={styles.tie}
                    style={{ background: `url(${tieImg?.url}) center / contain no-repeat` }}
                />
                <div 
                    className={styles.panel}
                    style={{ background: `url(${panelImg?.url}) center / contain no-repeat` }}
                >
                    <div>{user.name}</div>
                </div>
            </div>
            <div
                className={styles.characterWrapper}
                style={{ background: `url(${characterImg?.url}) center / contain no-repeat`}}
            >
                <div
                    className={characterEmoteImage ? styles.emote : styles.emoteHidden}
                    style={{
                        bottom: `${scrollValue}%`,
                        background: `url(${characterEmoteImage}) center / contain no-repeat`,
                    }}
                />
                <div className={styles.character} style={{
                    color: currentState === "good" ? AppColors.darkBlue : "white",
                    textShadow: currentState === "good" ? AppColors.gold : AppColors.darkRed,
                }}>
                   {scoreBuzz}
                </div>
            </div>
        </div>
    );
}

interface CharactersPlayingProps {
    players: User[];
    question?: Question;
    playersAnswer?: Answers;
    ioSocket?: Socket;
    showEmote: EmoteType[];
};

export default function CharactersPlaying(props: CharactersPlayingProps) {
    const { players, question, playersAnswer, ioSocket } = props;

    const { data: characterData } = useDefaultProducts();

    const [usersAnswer, setUsersAnswer] = useState<User[]>([]);

    useEffect(() => {
        function userAnswer(user: User, botDefaultWrong: boolean) {
            setUsersAnswer([...usersAnswer, user]);
        }

        if (ioSocket) {
            ioSocket.on('userAnswer', userAnswer);
        }

        return () => {
            if (ioSocket) {
                ioSocket.off('userAnswer', userAnswer);
            }
        }
    }, [usersAnswer]);

    useEffect(() => {
        if (playersAnswer) {
            setUsersAnswer([]);
        }
    }, [playersAnswer]);

    return (
        <div className={styles.charactersRow}>
            {characterData && players.map((player) => {
                let currentStatus: CharacterStatusType = "neutral";
                let textToDisplay = player.score.toString();

                if (playersAnswer) {
                    const answers = playersAnswer as ClassicAnswer;
                    answers.usersAnswer.forEach(userAnswer => {
                        if (userAnswer.userId === player.id) {
                            if (userAnswer.answer === answers.correctAnswer) {
                                textToDisplay = `+${(player.score - userAnswer.score) * -1}`;
                                currentStatus = "good";
                            } else {
                                textToDisplay = "0";
                                currentStatus = "wrong";
                            }
                        }
                    });
                } else if (usersAnswer.find(user => user.id === player.id)) {
                    currentStatus = "buzz";
                } else if (question && question.answers.length > 0) {
                    currentStatus = "thinking";
                }

                return (
                    <Character
                        key={player.id}
                        status={currentStatus}
                        user={player}
                        textToDisplay={textToDisplay}
                        characterData={characterData}
                        showEmote={props.showEmote}
                    />
                );
            })}
        </div>
    );
}